import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { Search, Bell, Mic, ChevronDown, User, Settings, LogOut, Shield, CheckCheck, Menu } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { EditProfileDialog } from "@/components/EntityDialogs";
import { cn } from "@/lib/utils";

const sevDot = { critical: "bg-rose-500", warning: "bg-amber-400", info: "bg-neutral-500" };

export const Header = ({ onVoiceClick, onMenuClick }) => {
  const navigate = useNavigate();
  const {
    currentUser, logout, notifications, unreadCount, markNotificationRead,
    markAllNotificationsRead, currentUser: u, viewAs,
  } = useFgp();
  const [q, setQ] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };
  const openNotif = (n) => { markNotificationRead(n.id); if (n.link) navigate(n.link); };

  return (
    <header className="h-16 sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-4 sm:px-6" data-testid="app-header">
      <button onClick={onMenuClick} data-testid="mobile-nav-toggle" aria-label="Open menu"
        className="lg:hidden h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors mr-1">
        <Menu className="h-5 w-5" />
      </button>
      <div className="relative w-full max-w-md hidden sm:block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people, documents, tasks…"
          data-testid="global-search"
          className="w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:bg-white" />
      </div>

      <div className="flex items-center gap-2 sm:gap-3 ml-auto">
        {!viewAs && (
          <button onClick={onVoiceClick} data-testid="header-voice-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-neutral-600 px-3 py-2 text-sm font-medium text-white hover:bg-neutral-700 transition-colors">
            <Mic className="h-4 w-4" /><span className="hidden sm:inline">New note</span>
          </button>
        )}

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="relative h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors" data-testid="header-notifications">
              <Bell className="h-[18px] w-[18px]" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center">{unreadCount}</span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80" data-testid="notifications-menu">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
              <button onClick={markAllNotificationsRead} className="text-xs text-neutral-600 font-medium flex items-center gap-1 hover:underline" data-testid="mark-all-read">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-80 overflow-y-auto">
              {notifications.map((n) => (
                <DropdownMenuItem key={n.id} onClick={() => openNotif(n)} data-testid={`notification-${n.id}`}
                  className={cn("flex items-start gap-2.5 px-2 py-2.5 cursor-pointer", !n.read && "bg-neutral-50/40")}>
                  <span className={cn("mt-1.5 h-2 w-2 rounded-full flex-shrink-0", sevDot[n.severity])} />
                  <div className="min-w-0">
                    <p className={cn("text-sm leading-snug", n.read ? "text-slate-600" : "font-semibold text-slate-800")}>{n.title}</p>
                    <p className="text-xs text-slate-400 truncate">{n.detail}</p>
                    <p className="text-[11px] text-slate-300 mt-0.5">{n.time}</p>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pl-1 sm:pl-2 sm:border-l border-slate-200" data-testid="header-profile">
              <Avatar className="h-9 w-9"><AvatarImage src={currentUser.avatar} alt={currentUser.name} /><AvatarFallback>{currentUser.name[0]}</AvatarFallback></Avatar>
              <div className="hidden md:block leading-tight text-left">
                <p className="text-sm font-semibold text-slate-800">{currentUser.name}</p>
                <p className="text-xs text-slate-400">{currentUser.role}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400 hidden md:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" data-testid="profile-menu">
            <DropdownMenuLabel>
              <p className="font-semibold text-slate-800">{currentUser.name}</p>
              <p className="text-xs font-normal text-slate-400">{currentUser.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid="menu-edit-profile" className="cursor-pointer gap-2"><User className="h-4 w-4" /> Edit profile</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/app/settings")} className="cursor-pointer gap-2"><Settings className="h-4 w-4" /> Settings</DropdownMenuItem>
            {u.isPlatformAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/admin")} data-testid="menu-platform-admin" className="cursor-pointer gap-2 text-[#516670] font-medium"><Shield className="h-4 w-4" /> Platform Admin</DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="cursor-pointer gap-2 text-rose-600 focus:text-rose-600"><LogOut className="h-4 w-4" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} />
    </header>
  );
};
